import { Check, Minus } from "lucide-react";
import { CtaBand } from "@/components/CtaBand";
import { PageHero } from "@/components/PageHero";
import { PillLink } from "@/components/PillLink";
import { Seo } from "@/components/Seo";
import { useReveals } from "@/lib/motion";
import { CONTACT_EMAIL, SIGNUP_URL } from "@/lib/site";

// Mirrors the plans in packages/shared-types/src/plans.ts and the plan table in
// the documentation. Keep the three in step.
const enquiry = (plan: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`MineOptics ${plan} plan`)}`;

const PLANS = [
  {
    name: "Demo",
    price: "$0",
    period: "",
    note: "Where every organization starts: every module for 7 days.",
    limits: ["1 project", "1 site", "1 user"],
    features: [
      "Every module for your first 7 days, from drillholes to an issued report",
      "A sample project: a fictional copper-gold study to look around in",
      "After 7 days: sites, drillholes, topography upload and the 3D view",
      "What you built is kept, and issued reports stay readable",
    ],
    cta: "Start free",
    to: SIGNUP_URL,
    featured: false,
  },
  {
    name: "Junior",
    price: "$25,000",
    period: "CAD · Annual",
    note: "Every module, for one project.",
    limits: ["1 project", "Unlimited sites", "Up to 10 users"],
    features: [
      "Every module, with no time limit",
      "The whole cost model: production, loading & hauling, fleet, processing, support services, revenue, lifecycle",
      "Block model import, 3D view and estimation",
      "Pit shell and yearly schedule, or a schedule you import",
      "Issued reports",
      "Operations: plan against actual",
      "Vendors & quotes",
      "Elevation sampling for a site",
    ],
    cta: "Talk to us",
    to: enquiry("Junior"),
    featured: true,
  },
  {
    name: "Enterprise",
    price: "$150,000",
    period: "CAD · Annual",
    note: "Every module, for a group with many projects.",
    limits: ["Unlimited projects", "Unlimited sites", "Up to 500 users"],
    features: ["Everything in Junior", "Any number of projects", "25% off professional services"],
    cta: "Talk to us",
    to: enquiry("Enterprise"),
    featured: false,
  },
];

const PLAN_NAMES = PLANS.map((plan) => plan.name);
// The table says what a plan has for good; for its first 7 days a Demo organization has every row.
const MATRIX_COLUMNS = ["Demo, after 7 days", ...PLAN_NAMES.slice(1)];

const MATRIX: { feature: string; included: boolean[] }[] = [
  { feature: "Projects, sites, drillholes, samples", included: [true, true, true] },
  { feature: "Topography upload and 3D view", included: [true, true, true] },
  { feature: "Reading and printing issued reports", included: [true, true, true] },
  { feature: "Cost model scenarios, the projection and scenario history", included: [false, true, true] },
  { feature: "Drilling & blasting", included: [false, true, true] },
  { feature: "Elevation sampling from an external dataset", included: [false, true, true] },
  { feature: "Vendors & quotes", included: [false, true, true] },
  { feature: "Production, imported schedule, loading & hauling, fleet, support services", included: [false, true, true] },
  { feature: "Processing, revenue, lifecycle", included: [false, true, true] },
  { feature: "Block models, estimation and grade-tonnage curves", included: [false, true, true] },
  { feature: "Pit shell and yearly schedule", included: [false, true, true] },
  { feature: "Issuing reports", included: [false, true, true] },
  { feature: "Operations: plan against actual", included: [false, true, true] },
];

export default function Pricing() {
  const scope = useReveals();

  return (
    <div ref={scope}>
      <Seo
        title="Pricing"
        description="MineOptics plans: start free with every module for 7 days, then Junior and Enterprise, each an annual contract with every module."
        path="/pricing"
      />
      <PageHero
        eyebrow="Pricing"
        title="Priced per organization."
        lede="Everyone in your organization works under one plan. Start free with every module for 7 days. Junior and Enterprise include every module and are annual contracts."
      />

      <section data-theme="light" className="bg-bg">
        <div className="shell py-20 md:py-28">
          <ul className="grid gap-4 lg:grid-cols-3">
            {PLANS.map((plan, index) => (
              <li
                key={plan.name}
                data-fade
                data-delay={index * 0.07}
                data-theme={plan.featured ? "dark" : undefined}
                className={`flex flex-col rounded-3xl border p-7 ${
                  plan.featured ? "border-transparent bg-bg" : "border-fg/15 bg-surface"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h2 className="eyebrow">{plan.name}</h2>
                  {plan.featured && <span className="eyebrow rounded-full bg-lime px-2.5 py-1 text-ink">Every module</span>}
                </div>
                <p className="mt-8 flex items-baseline gap-2">
                  <span className="text-4xl font-medium tracking-[-0.04em]">{plan.price}</span>
                  {plan.period && <span className="text-sm text-mute">{plan.period}</span>}
                </p>
                <p className="mt-3 min-h-[3rem] text-mute">{plan.note}</p>

                <ul className="mt-6 flex min-h-[4.25rem] flex-wrap content-start gap-1.5">
                  {plan.limits.map((limit) => (
                    <li key={limit} className="eyebrow rounded-full border border-fg/20 px-2.5 py-1.5">
                      {limit}
                    </li>
                  ))}
                </ul>

                <ul className="mb-8 mt-7 space-y-2.5 border-t border-fg/15 pt-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-3 text-[0.9375rem]">
                      <Check aria-hidden="true" className="mt-1 h-4 w-4 shrink-0 text-accent" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <PillLink
                  to={plan.to}
                  label={plan.cta}
                  variant={plan.featured ? "accent" : "solid"}
                  className="mt-auto w-full"
                />
              </li>
            ))}
          </ul>
          <p data-fade className="mt-6 max-w-[70ch] text-sm text-mute">
            Prices in Canadian dollars (CAD) for an annual contract, invoiced to the organization. To start a paid plan, write to
            us; we set it up on your organization.
          </p>
        </div>

        <div className="shell pb-28 md:pb-40">
          <h2 data-split className="title max-w-[14ch]">
            What each plan unlocks.
          </h2>
          <p data-fade className="mt-6 max-w-[70ch] text-sm text-mute">
            For its first 7 days a new organization has every row below. The Demo column is what stays open after that.
          </p>

          <div data-fade className="mt-14 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-left">
              <thead>
                <tr className="border-b border-fg/25">
                  <th scope="col" className="eyebrow py-4 pr-6 font-normal text-mute">
                    Feature
                  </th>
                  {MATRIX_COLUMNS.map((name) => (
                    <th key={name} scope="col" className="eyebrow w-[16%] py-4 text-center font-normal">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MATRIX.map((row) => (
                  <tr key={row.feature} className="border-b border-fg/15">
                    <th scope="row" className="py-5 pr-6 font-normal">
                      {row.feature}
                    </th>
                    {row.included.map((included, index) => (
                      <td key={PLAN_NAMES[index]} className="py-5 text-center">
                        {included ? (
                          <Check aria-label="Included" className="mx-auto h-5 w-5 text-accent" />
                        ) : (
                          <Minus aria-label="Not included" className="mx-auto h-5 w-5 text-fg/25" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p data-fade className="mt-10 max-w-[60ch] text-mute">
            Need something that is not on this page? Write to{" "}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-fg underline underline-offset-4">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
